import { Box, Flex, HStack, Heading, Spinner, Text } from "@chakra-ui/react"
import ScrollView from "@pathwright/ui/src/components/scroll/ScrollView"
import { flattenEdges, usePaginator } from "@pathwright/utils"
import ApolloError from "@pathwright/utils/src/graphql/ApolloError"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import OptionsMenu from "@pathwright/ux/src/components/menu/OptionsMenu"
import { useEffect, useMemo, useState } from "react"
import {
  InvitationContext,
  InvitationFragment,
  useInvitationsQuery
} from "@pathwright/graphql-client/src/generated"
import Invitation from "./Invitation"
import {
  JOINED_FILTER_LABEL,
  NOT_ACCEPTED_STATUS_MAP,
  NOT_JOINED_FILTER_LABEL
} from "./constants"

const tPrefix = "share_card.track_tab"

type FilterOption = {
  value: string
  label: string
}

const statusFilterOptions: FilterOption[] = [
  {
    value: "NOT_JOINED",
    label: NOT_JOINED_FILTER_LABEL
  },
  {
    value: "JOINED",
    label: JOINED_FILTER_LABEL
  },
  {
    value: "ALL",
    label: "All"
  }
]

const resolveStatus = (invitation: InvitationFragment) => {
  const invitation_status = invitation.invitation_status
  const emailStatus = invitation.invitation_email?.delivery_status

  if (invitation_status === "accepted") {
    return JOINED_FILTER_LABEL
  }

  return emailStatus && emailStatus in NOT_ACCEPTED_STATUS_MAP
    ? NOT_ACCEPTED_STATUS_MAP[emailStatus]
    : NOT_JOINED_FILTER_LABEL
}

// export function getInvitationsContext(context) {
//   switch (context.type) {
//     case "group":
//       return {
//         community_group_id: context.typeId
//       }
//     default:
//       return {
//         [`${context.type}_id`]: context.typeId
//       }
//   }
// }

const Invitations = ({ context }: { context?: InvitationContext | null }) => {
  const { tc, tca } = useTranslate()

  const filterOptions = useMemo(
    () =>
      statusFilterOptions.map((option) => {
        return {
          ...option,
          label: tc(`${tPrefix}.${option.label}`),
          onClick() {
            setCurrentStatusFilter(option)
          }
        }
      }),
    []
  )

  const [currentStatusFilter, setCurrentStatusFilter] = useState<FilterOption>(
    filterOptions[0]
  )

  const [invitations, setInvitations] = useState<InvitationFragment[]>([])

  const invitationsQuery = useInvitationsQuery({
    variables: {
      first: 300,
      context
    },
    fetchPolicy: "cache-and-network"
  })

  const { loading, error, data, refetch } = invitationsQuery

  const { loadMore, hasMore, loadingMore } = usePaginator({
    query: invitationsQuery,
    paginatorValue: invitationsQuery.data?.invitations
  })

  const doLoadMore = () => !loadingMore && hasMore && loadMore()

  useEffect(() => {
    if (data && data.invitations) {
      const nextInvitations = flattenEdges(data.invitations)
      setInvitations(nextInvitations)
    }
  }, [data])

  // TODO: use filtering on the server side, or load all invitations (not just first 300).
  const filteredInvitations = invitations
    ?.map((invitation) => {
      return {
        ...invitation,
        status: tc(`${tPrefix}.${resolveStatus(invitation)}`)
      }
    })
    .filter((invitation) => {
      if (currentStatusFilter.value === "ALL") return true

      if (
        currentStatusFilter.value === "JOINED" &&
        invitation.status === JOINED_FILTER_LABEL
      )
        return true

      if (
        currentStatusFilter.value === "NOT_JOINED" &&
        invitation.status !== JOINED_FILTER_LABEL
      )
        return true

      return false
    })

  return (
    <Box minH="300px">
      {error ? (
        <ApolloError error={error} />
      ) : data && invitations.length > 0 ? (
        <Box>
          <Flex justifyContent="space-between" alignItems="center">
            <Text color="gray.500" m={0}>
              {tca(`${tPrefix}.Sent Invites`)}
            </Text>
            <OptionsMenu
              options={filterOptions}
              selectedOptionLabel={currentStatusFilter.label}
              menuProps={{
                positioning: {
                  placement: "bottom-end"
                }
              }}
              triggerProps={{
                variant: "subtle"
              }}
            />
          </Flex>
          <ScrollView
            onScrollBottom={() => {
              doLoadMore()
            }}
          >
            <Box maxH="400px">
              {filteredInvitations.length ? (
                filteredInvitations.map((invitation, index) => (
                  <Invitation
                    key={`invitation-${index}`}
                    invitation={invitation}
                    onRevoke={refetch}
                    onResend={refetch}
                  />
                ))
              ) : (
                <Flex
                  minHeight="200px"
                  width="100%"
                  height="100%"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text color="gray.500" mt={4} w="full" textAlign="center">
                    No invitations found for filter "{currentStatusFilter.label}
                    "
                  </Text>
                </Flex>
              )}
              {loadingMore && (
                <HStack w="full" justifyContent="center">
                  <Spinner />
                </HStack>
              )}
            </Box>
          </ScrollView>
        </Box>
      ) : (
        <Flex
          minHeight="200px"
          width="100%"
          height="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {loading ? (
            <Spinner />
          ) : (
            <>
              <Heading
                // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
                size="h5"
                fontWeight="bold"
                m={0}
              >
                {tc(`${tPrefix}.Track Email Invites`)}
              </Heading>
              <Text textStyle="xs" color="gray.500" my={2}>
                {tc(
                  `${tPrefix}.Review and manage sent email invitations here.`
                )}
              </Text>
            </>
          )}
        </Flex>
      )}
    </Box>
  )
}

export default Invitations
